// The add-a-card flow (Settings, Cards and banks, "Add a card"): the pure rules and every line of
// its copy. One step per screen. The example alert is read by "teach me a bank" on the server
// (src/intake/pipeline.ts); nothing here is prefilled from the registry or from another card.
// Pure: no I/O. Tested in test/card-flow.test.ts.
import type { FilterChoice, SubjectWhy } from './filter-choice';

/** Steps, in the order a full path takes them. Steps that do not apply are skipped. */
export type FlowStep = 'gate' | 'alerts-on' | 'not-sure' | 'gmail' | 'example' | 'compare' | 'verify' | 'filter' | 'card-alerts' | 'done';

/** The gate's answers: "Does this bank email you when this card is used?" */
export type GateAnswer = 'yes' | 'no' | 'not-sure';

/**
 * After the gate is passed (alerts are on): which Gmail (only when Hush knows one already), then
 * the example. `known`: how many Gmail addresses Hush knows for this household.
 */
export const afterAlerts = (known: number): FlowStep => (known ? 'gmail' : 'example');

/** Where each gate answer goes next. "Not sure" leads to a Gmail search, then yes or no. */
export function afterGate(answer: GateAnswer, known: number): FlowStep {
  return answer === 'yes' ? afterAlerts(known) : answer === 'no' ? 'alerts-on' : 'not-sure';
}

/**
 * After choosing a Gmail address: one Hush has not verified for forwarding needs the
 * verification first (its forwarding confirmation is what teaches Hush the address), then the
 * example. A verified one goes straight to the example.
 */
export const afterGmail = (verified: boolean): FlowStep => (verified ? 'example' : 'verify');

/**
 * After the compare step (and verification, when it was needed): a new filter to make, else the
 * card's own alerts. A bank Hush already reads needs nothing in Gmail, only alerts turned on for
 * this card at the bank. The alert steps show once per flow: skipped when "Turn on transaction
 * alerts" was already shown (the gate said No, or Not sure then None).
 */
export function afterCompare(o: { kind: 'covered' | 'new'; needsVerify: boolean; alertsShown: boolean }): FlowStep {
  if (o.needsVerify) return 'verify';
  if (o.kind === 'new') return 'filter';
  return o.alertsShown ? 'done' : 'card-alerts';
}

// ---- Comparing the example with the filters Hush knows -----------------------------------------

export interface KnownFilter {
  bankId: string;
  bankName: string;
  senders: string[];
  /** Gmail subject query; empty (and no words) means From only. */
  subjectPattern: string;
  /** A "Has the words" phrase (no quotes), for a filter the flow made that way; else null. */
  words: string | null;
  /** The Gmail account the filter lives in; null when not known (older banks: any). */
  gmail: string | null;
}

export interface Example {
  sender: string;
  /** The subject pattern derived from the example (amounts, numbers and merchant removed). */
  subjectPattern: string;
  /** True when the subject had too little fixed wording (the general fallback words). */
  generalSubject: boolean;
  last4: string | null;
  /**
   * The filter chosen when the example was read (shared/filter-choice.ts). Null for an example read
   * before that existed: the subject pattern decides, as it used to.
   */
  filter?: FilterChoice | null;
}

export type CompareResult =
  | { kind: 'covered'; bankId: string; bankName: string }
  | {
      kind: 'new';
      from: string;
      /** For Gmail's Subject box; null when the subject cannot be used. */
      subject: string | null;
      /** For Gmail's "Has the words" box: the phrase, without quotes; null when not used. */
      words: string | null;
      /** With words: why the subject could not be used. */
      subjectWhy: SubjectWhy | null;
      /** Card-specific words were taken out of the subject (older examples only). */
      cardSpecific: boolean;
    };

/** Gmail subject query terms: each an OR list of words or phrases, all terms needed. */
export function queryTerms(pattern: string): string[][] {
  const terms: string[][] = [];
  const re = /\(([^)]*)\)|"([^"]*)"|(\S+)/g;
  for (const m of pattern.matchAll(re)) {
    if (m[1] !== undefined) {
      const alts = [...m[1].matchAll(/"([^"]*)"|(\S+)/g)].map((x) => (x[1] ?? x[2]).toLowerCase()).filter((x) => x && x !== 'or');
      if (alts.length) terms.push(alts);
    } else {
      const t = (m[2] ?? m[3]).toLowerCase();
      if (t && t !== 'or') terms.push([t]);
    }
  }
  return terms;
}

const words = (s: string) => ` ${s.toLowerCase().replace(/["()]/g, ' ').replace(/\s+/g, ' ').trim()} `;

/** Does a filter's subject query match an alert whose subject kept this fixed wording? */
export function subjectCovers(filterPattern: string, examplePattern: string): boolean {
  const text = words(examplePattern);
  return queryTerms(filterPattern).every((alts) => alts.some((a) => text.includes(` ${a.replace(/\s+/g, ' ').trim()} `)));
}

const sameAddress = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Words that mark a card-specific part of a subject ("ending in", "card ending"). */
const CARD_WORDS = /\b(ending|ends|last four|last 4)\b/i;

/**
 * The stable part of an example's subject: segments naming the card (its nickname, "ending in")
 * are left out. Null when nothing stable is left, or the subject had only general wording.
 */
export function stableSubject(e: Example, nickname: string | null): { subject: string | null; cardSpecific: boolean } {
  if (e.generalSubject || !e.subjectPattern.trim()) return { subject: null, cardSpecific: false };
  const nick = nickname?.trim().toLowerCase().split(/\s+/).filter((w) => w.length >= 3) ?? [];
  const segments = [...e.subjectPattern.matchAll(/"([^"]*)"|(\S+)/g)].map((m) => (m[1] ?? m[2]).trim()).filter(Boolean);
  let cardSpecific = false;
  const kept = segments
    .map((seg) => {
      if (CARD_WORDS.test(seg)) {
        cardSpecific = true;
        seg = seg.split(CARD_WORDS)[0].trim();
      }
      const ws = seg.split(/\s+/);
      const left = ws.filter((w) => !nick.includes(w.toLowerCase()));
      if (left.length !== ws.length) cardSpecific = true;
      return left.join(' ');
    })
    .filter((seg) => seg.replace(/[^a-z]/gi, '').length >= 3);
  if (!kept.length) return { subject: null, cardSpecific };
  return { subject: kept.map((seg) => (seg.includes(' ') ? `"${seg}"` : seg)).join(' '), cardSpecific };
}

/**
 * Compare the example with the filters Hush knows for this Gmail address. Covered: a filter with
 * its sender that lets it through (From only; a subject that matches; the same "Has the words"
 * phrase). New: the boxes to fill in: From, and Subject or Has the words (neither: From only).
 */
export function compareExample(e: Example, filters: KnownFilter[], gmail: string | null, nickname: string | null = null): CompareResult {
  const exampleWords = e.filter?.kind === 'words' ? e.filter.words.toLowerCase() : null;
  for (const f of filters) {
    if (f.gmail && gmail && !sameAddress(f.gmail, gmail)) continue;
    if (!f.senders.some((s) => sameAddress(s, e.sender))) continue;
    const covers = f.words ? exampleWords === f.words.toLowerCase() : !f.subjectPattern.trim() || subjectCovers(f.subjectPattern, e.subjectPattern);
    if (covers) return { kind: 'covered', bankId: f.bankId, bankName: f.bankName };
  }
  const from = e.sender.toLowerCase();
  if (e.filter?.kind === 'subject') {
    // The subject was found stable when read; the card's nickname may still be taken out of it.
    const stable = stableSubject(e, nickname);
    return { kind: 'new', from, subject: stable.subject ?? e.filter.subject, words: null, subjectWhy: null, cardSpecific: stable.cardSpecific };
  }
  if (e.filter) {
    const c = e.filter;
    return { kind: 'new', from, subject: null, words: c.kind === 'words' ? c.words : null, subjectWhy: c.kind === 'words' ? c.subjectWhy : null, cardSpecific: false };
  }
  const stable = stableSubject(e, nickname);
  return { kind: 'new', from, subject: stable.subject, words: null, subjectWhy: null, cardSpecific: stable.cardSpecific };
}

// ---- Gmail's forwarding confirmation -----------------------------------------------------------

/**
 * The one link that confirms forwarding in Gmail's email. The email carries two links on
 * mail-settings.google.com: ".../mail/vf-..." confirms ("click the link below to confirm the
 * request") and ".../mail/uf-..." cancels ("click this link to cancel this verification"). Only the
 * first is ever shown. Null when the email had no confirm link.
 */
export function confirmLink(links: readonly string[]): string | null {
  const settings = links.filter((l) => /^https:\/\/mail-settings\.google\.com\//i.test(l));
  return settings.find((l) => /\/mail\/vf-/i.test(l)) ?? settings.find((l) => !/\/mail\/uf-/i.test(l)) ?? null;
}

// ---- Copy (one sentence per screen where possible) ------------------------------------------

export const FLOW_TITLE = 'Add a card';
export const EVERY_CARD = 'Add each card you want Hush to track. A card from a bank Hush already reads often needs nothing in Gmail.';
export const GATE_Q = 'Does this bank email you when this card is used?';
export const ALERTS_ON_TITLE = 'Turn on transaction alerts';
export const ALERTS_TO_GMAIL = 'Send the alerts to the Gmail address Hush will forward from.';
export const NOT_SURE_TITLE = 'Check your Gmail';
export const NOT_SURE_BODY = 'Search Gmail for the bank name and look for an email about a recent purchase.';
export const GMAIL_Q = "Which Gmail gets this card's alerts?";
export const GMAIL_OTHER = 'A different Gmail address';
export const EXAMPLE_TITLE = 'Forward one alert';
export const EXAMPLE_BODY = 'Open one real alert for this card in Gmail and forward it to your Hush address.';
export const EXAMPLE_WAIT = 'Waiting for your alert';
export const COMPARE_TITLE = 'Checking your alert';
/** On the card's alerts step, when a filter Hush knows already covers the bank. */
export const COVERED = 'Hush already reads alerts from this bank, so there is nothing to do in Gmail.';
export const CARD_SPECIFIC = 'Hush left out the part of the subject that names this card, so one filter fits all your cards from this bank.';
export const NICKNAME_LABEL = 'Name this card (optional)';
export const VERIFY_TITLE = 'Let Gmail forward to Hush';
export const VERIFY_WAIT = "Waiting for Gmail's confirmation email";
export const VERIFY_ARRIVED = "Gmail's confirmation email arrived";
export const VERIFY_LINK = 'Copy this link, open it in the browser where you are signed in to Gmail, and choose Confirm.';
export const VERIFY_NO_LINK = "Hush could not find the confirmation link in Gmail's email. In Gmail, add your Hush address as a forwarding address again to get a new one.";
export const VERIFY_DONE = 'I confirmed it';
export const FILTER_TITLE = 'Make the Gmail filter';
export const FILTER_COMPUTER = "Do this on a computer: Gmail's phone app cannot make filters.";
export const FILTER_OPEN = (gmail: string | null) => `In Gmail${gmail ? ` (${gmail})` : ''}, open the search options and make a new filter.`;
export const FILTER_FIELDS_ONLY = 'Fill in only these boxes, then choose Create filter.';
export const FILTER_WORDS_LABEL = 'Has the words';
export const FILTER_WORDS_HOW = 'Paste it into the "Has the words" box, below Subject, with the quotes. The quotes keep the words together as one phrase.';
/** Why this filter: the bank sends other email too, and this catches only its purchase alerts. */
export const FILTER_WHY_WORDS = (bank: string) =>
  `${bank} also sends statements, offers and security notices. This phrase appears in every purchase alert, so only those are forwarded.`;
export const FILTER_WHY_SUBJECT = (bank: string) =>
  `${bank} also sends statements, offers and security notices. This subject is the same on every purchase alert, so only those are forwarded.`;
/** Why the subject could not be used, when the filter uses a phrase (shared/filter-choice.ts SubjectWhy). */
export const SUBJECT_WHY: Record<SubjectWhy, (bank: string) => string> = {
  purchase: () => "The subject is different every time because it includes the amount and the store, so it can't be used.",
  changes: () => "The subject is different every time because it includes the date or the card number, so it can't be used.",
  general: (bank) => `The subject is the same on other ${bank} emails too, so it can't pick out purchases.`,
};
/** From only: nothing stable was found. Every email from the sender reaches Hush; anything not a purchase is ignored. */
export const FROM_ONLY_WARNING = (sender: string) =>
  `Hush could not find wording that is only in purchase alerts, so this filter uses From only. Every email from ${sender} will be forwarded to Hush, including statements and offers. Hush reads each one and ignores anything that is not a purchase.`;
export const FILTER_FORWARD = 'Tick Forward it to and pick your Hush address.';
export const FILTER_LEAVE_FORWARDING = 'Leave Gmail\'s "Disable forwarding" setting alone.';
export const CARD_ALERTS_TITLE = 'Turn on alerts for this card';
export const CARD_ALERTS_BODY = 'Your bank sends alerts card by card. Check this one is on.';
/** Cards and banks, under a Working bank with several cards: the filter is per bank, alerts per card. */
export const ONE_FILTER_NOTE = (bank: string) => `One Gmail filter covers all ${bank} cards. Each card still needs alerts turned on in ${bank}.`;
export const DONE_TITLE = "You're all set";
export const DONE_BODY = 'The next time you use this card, the purchase shows up in Hush by itself. Nothing else to do.';
/** Every automatic check in the flow carries this label: nothing here is ticked by hand. */
export const HUSH_CHECKS = 'Hush checks this';

/**
 * "Not working?" help for each step: placeholders for step 3 of this work, which fills them in.
 * A null entry shows no link.
 */
export const NOT_WORKING_HELP: Record<FlowStep, string | null> = {
  gate: null,
  'alerts-on': null,
  'not-sure': null,
  gmail: null,
  example: null,
  compare: null,
  verify: null,
  filter: null,
  'card-alerts': null,
  done: null,
};
